import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, Upload, Sparkles, Settings, LogOut, Layers, Library, CreditCard, Shield, X
} from 'lucide-react';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/upload', icon: Upload, label: 'Upload' },
  { to: '/library', icon: Library, label: 'Library' },
  { to: '/pipeline', icon: Layers, label: 'Pipeline' },
  { to: '/presets', icon: Sparkles, label: 'Presets' },
  { to: '/pricing', icon: CreditCard, label: 'Pricing' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const adminNav = { to: '/admin', icon: Shield, label: 'Admin' };

export const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const avatarSrc = user?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : null;

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={onClose}
          className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
          data-testid="sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen w-64 border-r border-border bg-zinc-950/95 backdrop-blur-xl flex flex-col transition-transform duration-300 ease-out',
          'md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        data-testid="sidebar"
        aria-label="Main navigation"
      >
        {/* Logo + mobile close */}
        <div className="flex items-center justify-between px-4 py-5 border-b border-border/50">
          <img src="/logo-white-wide.png" alt="ClipParrot" className="h-14 object-contain" />
          <button
            onClick={onClose}
            className="md:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Close menu"
            data-testid="sidebar-close-button"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/dashboard'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                )
              }
              data-testid={`sidebar-nav-${label.toLowerCase()}`}
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <NavLink
              to={adminNav.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    : 'text-muted-foreground hover:text-amber-400 hover:bg-amber-500/5'
                )
              }
              data-testid="sidebar-nav-admin"
            >
              <adminNav.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {adminNav.label}
              <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-400/70">
                {user?.role === 'super_admin' ? 'Super' : 'Admin'}
              </span>
            </NavLink>
          )}
        </nav>

        {/* User + Logout */}
        <div className="px-3 pb-6 space-y-2">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
              }`
            }
            data-testid="sidebar-profile"
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="w-5 h-5 rounded-full object-cover" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-[9px] font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
            <span className="truncate">{user?.name || 'Profile'}</span>
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200 w-full"
            data-testid="logout-button"
          >
            <LogOut className="w-[18px] h-[18px]" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};
